import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  isMonthBeforePlanMin,
  isYearBeforePlanMin,
} from "../../helpers/dateRangePlanLimits";
import CalendarView from "./calendarView";
import MonthPickView from "./monthPickView";
import YearPickView from "./yearPickView";
import TimePickView from "./timepickView";
import DateTimeToggle from "./dateTimeToggle";

interface IInlineDatepickerProps {
  currDate: Date;
  setDate: (date: Date) => void;
  pickType: "start" | "end";
  hour: number;
  setHour: (hour: number) => void;
  minute: number;
  setMinute: (minute: number) => void;
  disableDateTimeDetails?: {
    hour: number;
    minute: number;
    date: Date;
  };
  planMinDate?: Date;
  onPlanLimitInteract?: () => void;
}

const InlineDatepicker = ({
  currDate,
  setDate,
  pickType,
  hour,
  setHour,
  minute,
  setMinute,
  disableDateTimeDetails,
  planMinDate,
  onPlanLimitInteract,
}: IInlineDatepickerProps) => {
  const [month, setMonth] = useState(new Date().getMonth());
  const [day, setDay] = useState(new Date().getDate());
  const [year, setYear] = useState(new Date().getFullYear());

  const [showCalendarView, setShowCalendarView] = useState(true);
  const [showMonthView, setShowMonthView] = useState(false);
  const [showYearView, setShowYearView] = useState(false);
  const [showTimePickView, setShowTimePickView] = useState(false);

  const preTimePickerViewState = useRef<boolean[]>([
    showCalendarView,
    showMonthView,
    showYearView,
  ]);

  useEffect(() => {
    if (currDate) {
      setDay(new Date(currDate).getDate());
      setMonth(new Date(currDate).getMonth());
      setYear(new Date(currDate).getFullYear());
    }
  }, [currDate]);

  const handleDateChange = (date: Date) => {
    setDay(new Date(date).getDate());
    setMonth(new Date(date).getMonth());
    setYear(new Date(date).getFullYear());
  };

  const handleMonthChange = (date: Date) => {
    if (pickType === "start" && isMonthBeforePlanMin(date, planMinDate)) {
      onPlanLimitInteract?.();
      return;
    }

    setMonth(new Date(date).getMonth());
    setYear(new Date(date).getFullYear());
    setShowMonthView(false);
    setShowCalendarView(true);
    setShowYearView(false);
  };

  const handleYearChange = (date: Date) => {
    if (pickType === "start" && isYearBeforePlanMin(date, planMinDate)) {
      onPlanLimitInteract?.();
      return;
    }

    setYear(new Date(date).getFullYear());
    setShowMonthView(false);
    setShowCalendarView(true);
    setShowYearView(false);
  };

  const handleHourChange = (value: string) => {
    setHour(parseInt(value));
  };

  const handleMinuteChange = (value: string) => {
    setMinute(parseInt(value));
  };

  const handleDateSelect = (selectedDate: Date) => {
    setDate(selectedDate);
    document.querySelector(".date-pick-wrapper")?.click();
  };

  const handleShowMonthView = () => {
    setShowMonthView(true);
    setShowCalendarView(false);
    setShowYearView(false);
  };

  const handleShowYearView = () => {
    setShowMonthView(false);
    setShowCalendarView(false);
    setShowYearView(true);
  };

  const handleDateTimeSelectionToggle = () => {
    if (view.isTimePicker) {
      // Load last view state when time picker was hidden
      const [shouldShowCalendar, shouldShowMonthPicker, shouldShowYearPicker] =
        preTimePickerViewState.current;
      setShowMonthView(shouldShowMonthPicker);
      setShowCalendarView(shouldShowCalendar);
      setShowYearView(shouldShowYearPicker);
      setShowTimePickView(false);
    } else {
      // Save last view state before showing time picker view
      preTimePickerViewState.current = [
        showCalendarView,
        showMonthView,
        showYearView,
      ];
      setShowMonthView(false);
      setShowCalendarView(false);
      setShowYearView(false);
      setShowTimePickView(true);
    }
  };

  const view = {
    isTimePicker:
      showTimePickView && !showCalendarView && !showMonthView && !showYearView,
    isCalendar:
      !showTimePickView && showCalendarView && !showMonthView && !showYearView,
    isMonthPicker:
      !showTimePickView && !showCalendarView && showMonthView && !showYearView,
    isYearPicker:
      !showTimePickView && !showCalendarView && !showMonthView && showYearView,
  };

  const disableDateTimeConfig = useMemo(() => {
    const isSameDay = disableDateTimeDetails?.date && currDate && 
      new Date(disableDateTimeDetails.date).getDate() === new Date(currDate).getDate() &&
      new Date(disableDateTimeDetails.date).getMonth() === new Date(currDate).getMonth() &&
      new Date(disableDateTimeDetails.date).getFullYear() === new Date(currDate).getFullYear();
    return {
      date: disableDateTimeDetails?.date,
      hour: isSameDay ? disableDateTimeDetails?.hour : null,
      minute: isSameDay ? disableDateTimeDetails?.minute : null,
    };
  }, [disableDateTimeDetails, currDate]);

  return (
    <Wrapper className="inline-date-picker">
      {view.isTimePicker ? (
        <TimePickView
          hour={hour}
          minute={minute}
          onHourChange={handleHourChange}
          onMinuteChange={handleMinuteChange}
          selectedDate={new Date(year, month, day)}
          onDateChange={handleDateChange}
          disableHour={disableDateTimeConfig.hour}
          disableMinute={disableDateTimeConfig.minute}
          pickType={pickType}
        />
      ) : null}
      {view.isCalendar ? (
        <CalendarView
          selectedDate={{ year, month, day }}
          onDateChange={handleDateChange}
          onDateSelect={handleDateSelect}
          onShowMonthView={handleShowMonthView}
          pickerType={pickType}
          disableDate={disableDateTimeConfig.date}
          planMinDate={pickType === "start" ? planMinDate : undefined}
          onPlanLimitInteract={onPlanLimitInteract}
        />
      ) : null}
      {view.isMonthPicker ? (
        <MonthPickView
          selectedDate={{ year, month, day }}
          onMonthChange={handleMonthChange}
          onShowYearView={handleShowYearView}
          minDate={pickType === "end" ? disableDateTimeConfig.date : undefined}
          maxDate={pickType === "start" ? disableDateTimeConfig.date : undefined}
          planMinDate={pickType === "start" ? planMinDate : undefined}
          onPlanLimitInteract={onPlanLimitInteract}
        />
      ) : null}
      {view.isYearPicker ? (
        <YearPickView
          selectedDate={{ year, month, day }}
          onYearChange={handleYearChange}
          minDate={pickType === "end" ? disableDateTimeConfig.date : undefined}
          maxDate={pickType === "start" ? disableDateTimeConfig.date : undefined}
          planMinDate={pickType === "start" ? planMinDate : undefined}
          onPlanLimitInteract={onPlanLimitInteract}
        />
      ) : null}
      <DateTimeToggle
        showTimePicker={!view.isTimePicker}
        onClick={handleDateTimeSelectionToggle}
      />
    </Wrapper>
  );
};

export default InlineDatepicker;

const Wrapper = styled.div`
  margin: 15px 0px 0;
  flex: 1;
  border: 1px solid ${({ theme }) => theme.border_color};
  border-radius: 5px;
`;
